/* Bezorging management page - uses the same page frame as the Beheerders admin screen.
   Updated: Added a Levertijd card to set the number of working days between ordering and delivery
   and the daily order cutoff hour, with a live example of the resulting earliest delivery day. */

import { useState, useEffect } from 'react';
import Breadcrumb from '@/components/admin/Breadcrumb';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { Truck, Save, Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Package, CalendarClock } from 'lucide-react';
import { format, addDays, startOfWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { DEFAULT_LEAD_TIME, getEarliestDeliveryDate, type DeliveryLeadTime } from '@/lib/delivery-date';

interface OegemaPricing {
  [key: string]: number;
}

const PALLET_PLACE_KEYS = Array.from({ length: 15 }, (_, i) => {
  const value = i + 1;
  return {
    key: `pallet_place_${value}`,
    label: `${value}`,
    value,
  };
});

export default function DeliverySettingsPage() {
  const [oegemaPricing, setOegemaPricing] = useState<OegemaPricing>({});
  const [palletboxCostCents, setPalletboxCostCents] = useState<number>(3000);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingPalletbox, setIsSavingPalletbox] = useState(false);
  const [isSavingOegema, setIsSavingOegema] = useState(false);
  const [palletboxMessage, setPalletboxMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [oegemaMessage, setOegemaMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [leadTime, setLeadTime] = useState<DeliveryLeadTime>(DEFAULT_LEAD_TIME);
  const [isSavingLeadTime, setIsSavingLeadTime] = useState(false);
  const [leadTimeMessage, setLeadTimeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('delivery_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['oegema_pricing', 'kosten_palletbox', 'delivery_lead_time']);

      if (error) throw error;

      for (const item of data || []) {
        if (item.setting_key === 'oegema_pricing') {
          setOegemaPricing(item.setting_value || {});
        } else if (item.setting_key === 'kosten_palletbox') {
          setPalletboxCostCents(item.setting_value?.cost_per_palletbox_cents || 3000);
        } else if (item.setting_key === 'delivery_lead_time') {
          setLeadTime({
            lead_working_days: Number(item.setting_value?.lead_working_days ?? DEFAULT_LEAD_TIME.lead_working_days),
            cutoff_hour: Number(item.setting_value?.cutoff_hour ?? DEFAULT_LEAD_TIME.cutoff_hour),
          });
        }
      }
    } catch {
      setPalletboxMessage({ type: 'error', text: 'Fout bij het ophalen van instellingen' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePalletbox = async () => {
    setIsSavingPalletbox(true);
    setPalletboxMessage(null);

    try {
      const { error } = await supabase
        .from('delivery_settings')
        .update({ setting_value: { cost_per_palletbox_cents: palletboxCostCents } })
        .eq('setting_key', 'kosten_palletbox');

      if (error) throw error;

      setPalletboxMessage({ type: 'success', text: 'Palletbox-kosten opgeslagen' });
    } catch {
      setPalletboxMessage({ type: 'error', text: 'Fout bij het opslaan' });
    } finally {
      setIsSavingPalletbox(false);
    }
  };

  const handleSaveLeadTime = async () => {
    setLeadTimeMessage(null);
    const { lead_working_days, cutoff_hour } = leadTime;
    if (!Number.isInteger(lead_working_days) || lead_working_days < 1 || lead_working_days > 20) {
      setLeadTimeMessage({ type: 'error', text: 'Aantal werkdagen moet tussen 1 en 20 liggen' });
      return;
    }
    if (!Number.isInteger(cutoff_hour) || cutoff_hour < 1 || cutoff_hour > 24) {
      setLeadTimeMessage({ type: 'error', text: 'Besteltijd moet een heel uur tussen 1 en 24 zijn' });
      return;
    }
    setIsSavingLeadTime(true);
    try {
      const { error } = await supabase
        .from('delivery_settings')
        .update({ setting_value: { lead_working_days, cutoff_hour } })
        .eq('setting_key', 'delivery_lead_time');
      if (error) throw error;
      setLeadTimeMessage({ type: 'success', text: 'Levertijd opgeslagen' });
    } catch {
      setLeadTimeMessage({ type: 'error', text: 'Fout bij het opslaan' });
    } finally {
      setIsSavingLeadTime(false);
    }
  };

  const leadTimeExamples = (() => {
    const monday = startOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 });
    return [0, 4].map((offset) => {
      const orderDay = addDays(monday, offset);
      orderDay.setHours(12, 0, 0, 0);
      return {
        order: format(orderDay, 'EEEE', { locale: nl }),
        delivery: format(getEarliestDeliveryDate(leadTime, orderDay), 'EEEE', { locale: nl }),
      };
    });
  })();

  const handleSaveOegema = async () => {
    setIsSavingOegema(true);
    setOegemaMessage(null);

    try {
      const { error } = await supabase
        .from('delivery_settings')
        .update({ setting_value: oegemaPricing })
        .eq('setting_key', 'oegema_pricing');

      if (error) throw error;

      setOegemaMessage({ type: 'success', text: 'Oegema tarieven opgeslagen' });
    } catch {
      setOegemaMessage({ type: 'error', text: 'Fout bij het opslaan' });
    } finally {
      setIsSavingOegema(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-12">
        <Breadcrumb pageName="Bezorging" />
        <h1 className="mb-6 text-4xl font-bold text-jv-primary">Bezorging</h1>
        <div className="overflow-hidden rounded-2xl bg-white shadow-md">
          <div className="p-8 text-center">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-12">
      <Breadcrumb pageName="Bezorging" />
      <h1 className="mb-2 text-4xl font-bold text-jv-primary">Bezorging</h1>
      <p className="mb-6 text-gray-600">Levertijd, Oegema tarieven en palletbox-kosten beheren</p>

      <div className="space-y-6">

      {/* Levertijd */}
      <Card className="rounded-2xl shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5" />
              Levertijd
            </CardTitle>
            <Button onClick={handleSaveLeadTime} disabled={isSavingLeadTime} size="sm" className="bg-[#1e2b22] text-white hover:bg-[#1e2b22]/90">
              {isSavingLeadTime ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Opslaan
            </Button>
          </div>
          {leadTimeMessage && (
            <Alert
              variant={leadTimeMessage.type === 'error' ? 'destructive' : 'default'}
              className={`mt-3 ${leadTimeMessage.type === 'success' ? 'border-green-200 bg-green-50' : ''}`}
            >
              {leadTimeMessage.type === 'success' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription className={leadTimeMessage.type === 'success' ? 'text-green-800' : ''}>
                {leadTimeMessage.text}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            De klant kiest bij het afrekenen zelf een bezorgdag. Alleen werkdagen (maandag t/m vrijdag) tellen mee; Nederlandse feestdagen worden overgeslagen.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 max-w-xl">
            <div className="space-y-2">
              <Label htmlFor="lead-days">Werkdagen na besteldag</Label>
              <Input
                id="lead-days"
                type="number"
                min={1}
                max={20}
                value={leadTime.lead_working_days}
                onChange={(e) => setLeadTime(prev => ({ ...prev, lead_working_days: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cutoff-hour">Besteld voor (uur)</Label>
              <Input
                id="cutoff-hour"
                type="number"
                min={1}
                max={24}
                value={leadTime.cutoff_hour}
                onChange={(e) => setLeadTime(prev => ({ ...prev, cutoff_hour: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>
          {leadTime.lead_working_days >= 1 && (
            <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700 space-y-1">
              {leadTimeExamples.map((ex) => (
                <p key={ex.order}>
                  Op <span className="font-medium">{ex.order}</span> besteld voor {leadTime.cutoff_hour}:00 uur, op z'n vroegst bezorgd op <span className="font-medium">{ex.delivery}</span>
                </p>
              ))}
              <p className="text-xs text-gray-500 pt-1">Na {leadTime.cutoff_hour}:00 uur of in het weekend telt de volgende werkdag als besteldag.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kosten palletbox */}
      <Card className="rounded-2xl shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Kosten palletbox
            </CardTitle>
            <Button onClick={handleSavePalletbox} disabled={isSavingPalletbox} size="sm" className="bg-[#1e2b22] text-white hover:bg-[#1e2b22]/90">
              {isSavingPalletbox ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Opslaan
            </Button>
          </div>
          {palletboxMessage && (
            <Alert
              variant={palletboxMessage.type === 'error' ? 'destructive' : 'default'}
              className={`mt-3 ${palletboxMessage.type === 'success' ? 'border-green-200 bg-green-50' : ''}`}
            >
              {palletboxMessage.type === 'success' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription className={palletboxMessage.type === 'success' ? 'text-green-800' : ''}>
                {palletboxMessage.text}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <CardContent>
          <div className="max-w-xs space-y-2">
            <Label>Kosten per palletbox (centen)</Label>
            <Input
              type="number"
              min={0}
              value={palletboxCostCents}
              onChange={(e) => setPalletboxCostCents(parseInt(e.target.value) || 0)}
            />
            <p className="text-sm text-gray-500">
              = EUR {(palletboxCostCents / 100).toFixed(2)} per palletbox
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Formule: aantal benodigde boxen x kosten per palletbox = totale dooskosten
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Oegema pricing */}
      <Card className="rounded-2xl shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Oegema tarieven per palletplaats
            </CardTitle>
            <Button onClick={handleSaveOegema} disabled={isSavingOegema} size="sm" className="bg-[#1e2b22] text-white hover:bg-[#1e2b22]/90">
              {isSavingOegema ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Opslaan
            </Button>
          </div>
          {oegemaMessage && (
            <Alert
              variant={oegemaMessage.type === 'error' ? 'destructive' : 'default'}
              className={`mt-3 ${oegemaMessage.type === 'success' ? 'border-green-200 bg-green-50' : ''}`}
            >
              {oegemaMessage.type === 'success' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription className={oegemaMessage.type === 'success' ? 'text-green-800' : ''}>
                {oegemaMessage.text}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Maximaal 15 palletplaatsen per bestelling. Bestellingen boven dit maximum krijgen de melding om contact op te nemen.
          </p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Palletplaatsen</TableHead>
                  <TableHead className="w-40">Prijs (EUR excl. BTW)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {PALLET_PLACE_KEYS.map(({ key, label, value }) => (
                  <TableRow key={key} className={value % 1 === 0 ? 'bg-gray-50' : ''}>
                    <TableCell className="font-medium">{label}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        className="w-32 h-8 text-sm"
                        value={oegemaPricing[key] ?? ''}
                        onChange={(e) => {
                          setOegemaPricing(prev => ({
                            ...prev,
                            [key]: parseFloat(e.target.value) || 0,
                          }));
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
